import React from 'react';
import LegalLayout from './LegalLayout';
import { Helmet } from 'react-helmet-async';

export default function Disclaimer() {
  return (
    <>
      <Helmet>
        <title>Medical Disclaimer | GlowWise</title>
        <meta name="description" content="GlowWise provides wellness guidance, not medical advice. Always consult a qualified healthcare professional before making health decisions." />
      </Helmet>
      <LegalLayout
      eyebrow="Medical Disclaimer"
      title={<>Wellness, <em style={{ fontStyle: 'italic', color: '#6B9E7F' }}>not medical advice.</em></>}
      lastUpdated="26 April 2026"
    >
      <p>This Medical Disclaimer applies to all use of GlowWise, including the AI Coach, daily check-ins, insights, recommendations, and any related content. Please read it carefully — your health and safety depend on understanding what GlowWise is, and what it isn't.</p>

      <div className="warning-box">
        <strong>Important:</strong> If you are experiencing a medical emergency, stop reading and call your local emergency services immediately. In the UK, dial 999 or 111. GlowWise is not an emergency service and cannot help in urgent medical situations.
      </div>

      <h2>1. GlowWise is not medical advice</h2>
      <p>GlowWise provides general wellness and lifestyle guidance generated by artificial intelligence and based on the information you share. The content is for educational and informational purposes only.</p>
      <p><strong>GlowWise does not, and cannot:</strong></p>
      <ul>
        <li>Provide medical advice, diagnosis, or treatment</li>
        <li>Replace a consultation with a qualified healthcare professional</li>
        <li>Identify or assess medical conditions</li>
        <li>Prescribe medication, supplements, or therapies</li>
        <li>Account for your full medical history, lab results, or current medications</li>
      </ul>

      <h2>2. Always consult a healthcare professional</h2>
      <p>Before making any significant change to your diet, exercise, supplements, sleep medication, or general lifestyle — especially if it relates to a health concern — you should consult a qualified healthcare provider, such as:</p>
      <ul>
        <li>Your GP (general practitioner)</li>
        <li>A specialist doctor</li>
        <li>A registered pharmacist</li>
        <li>A registered dietitian or nutritionist</li>
        <li>A qualified mental health professional</li>
      </ul>
      <p>Never disregard professional medical advice or delay seeking it because of something you read on GlowWise.</p>

      <h2>3. High-risk individuals</h2>
      <p>You must seek professional medical advice before following any guidance from GlowWise if you are:</p>
      <ul>
        <li>Pregnant, breastfeeding, or trying to conceive</li>
        <li>Under 18 (GlowWise is not for minors)</li>
        <li>Living with a chronic medical condition (e.g. diabetes, autoimmune conditions, heart disease, kidney disease)</li>
        <li>Taking prescription medication</li>
        <li>Experiencing or recovering from an eating disorder</li>
        <li>Recovering from surgery or a serious illness</li>
        <li>Receiving treatment from a doctor or therapist</li>
      </ul>
      <p>GlowWise is not designed for clinical populations and may not be appropriate for your situation.</p>

      <h2>4. Mental health</h2>
      <p>GlowWise can discuss general topics like stress, sleep, and mood. It is <strong>not</strong> a substitute for therapy, counselling, or psychiatric care. If you are experiencing depression, anxiety, suicidal thoughts, an eating disorder, addiction, or any other mental health condition, please contact a qualified mental health professional.</p>
      <p>In the UK, you can contact:</p>
      <ul>
        <li><strong>Samaritans:</strong> 116 123 (free, 24/7)</li>
        <li><strong>NHS 111:</strong> for non-emergency mental health support</li>
        <li><strong>Mind:</strong> 0300 123 3393 (Mon–Fri 9am–6pm)</li>
        <li><strong>Beat (eating disorders):</strong> 0808 801 0677</li>
      </ul>

      <h2>5. Supplements and nutrition</h2>
      <p>If GlowWise mentions supplements, vitamins, herbs, or dietary changes, this information is general in nature. Supplements:</p>
      <ul>
        <li>Are not regulated as strictly as medications</li>
        <li>Can interact with prescription drugs and other supplements</li>
        <li>May not be safe for everyone, including those with medical conditions or allergies</li>
        <li>Should never replace prescribed medication</li>
      </ul>
      <p>Always speak to your GP or pharmacist before starting any new supplement.</p>

      <h2>6. AI limitations</h2>
      <p>GlowWise's AI Coach uses large language models to generate responses. AI systems can:</p>
      <ul>
        <li>Make mistakes or provide inaccurate information</li>
        <li>Misunderstand your question or context</li>
        <li>Reflect biases present in their training data</li>
        <li>Be confidently wrong</li>
      </ul>
      <p>Always apply your own judgement and verify important information with a qualified professional before acting on it.</p>

      <h2>7. No doctor-patient relationship</h2>
      <p>Using GlowWise does not create any doctor-patient, therapist-client, or professional advisory relationship between you and GlowWise, its team, or any party connected to the service.</p>

      <h2>8. Personal responsibility</h2>
      <p>By using GlowWise, you acknowledge and accept that:</p>
      <ul>
        <li>You are solely responsible for your health decisions and outcomes</li>
        <li>Any action you take based on GlowWise content is at your own risk</li>
        <li>You will not hold GlowWise or its team responsible for any health outcomes</li>
      </ul>

      <h2>9. Limitation of liability</h2>
      <p>To the fullest extent permitted by UK law, GlowWise and its team disclaim all liability for any harm, loss, injury, or damage — direct or indirect — arising from your use of, or reliance on, the app or its content.</p>
      <p>Nothing in this disclaimer limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be lawfully excluded.</p>

      <h2>10. Emergencies</h2>
      <p>GlowWise must never be used in a medical emergency. If you or someone you know is experiencing:</p>
      <ul>
        <li>A medical emergency (chest pain, severe injury, loss of consciousness)</li>
        <li>A mental health crisis or thoughts of self-harm</li>
        <li>Severe allergic reactions</li>
        <li>Symptoms of stroke or heart attack</li>
      </ul>
      <p><strong>Call 999 (UK) or your local emergency number immediately.</strong></p>

      <h2>11. Updates</h2>
      <p>We may update this disclaimer at any time. Continued use of GlowWise indicates your acceptance of the latest version.</p>

      <h2>12. Contact</h2>
      <p>If you have questions about this disclaimer, email <a href="mailto:support@glowwise.app">support@glowwise.app</a>.</p>
    </LegalLayout>
    </>
  );
}
